/**
 * BC: Hero slider (Basturk.co · basturk.co)
 *
 * Horizon'un slideshow'una bağlı değil — kendi custom element'imiz.
 * Tasarım kararları:
 *   · İlk slayt Liquid'de `is-active` ile geliyor → JS yüklenmese de hero dolu.
 *   · Otomatik geçiş sekme gizliyken, fare üstündeyken, odak içerideyken ve
 *     `prefers-reduced-motion` açıkken durur.
 *   · İlerleme çubuğu CSS animasyonu; her geçişte JS restart ediyor.
 *
 * ⚠️ `type="module"` ile yükleniyor (defer gibi davranır). `script_tag`
 * kullanılmamalı — senkron basıldığında connectedCallback slaytlar DOM'a
 * girmeden çalışıyor ve butonlar ölü kalıyor (renk tablosunda yaşandı).
 */
class BcHeroSlider extends HTMLElement {
  #index = 0;
  #zamanlayici = null;
  #duraklat = new Set();
  #ilkCizimBitti = false;

  connectedCallback() {
    this.slaytlar = [...this.querySelectorAll('[data-bc-slide]')];

    // Element açılış etiketi parse edilir edilmez bağlanabilir; slaytlar
    // henüz basılmamışsa bir kare bekle.
    if (this.slaytlar.length === 0) {
      requestAnimationFrame(() => this.connectedCallback());
      return;
    }

    this.cubuklar = [...this.querySelectorAll('[data-bc-cubuk]')];
    this.sure = Number(this.dataset.sure || 6000);
    this.otomatik = this.dataset.otomatik === 'true' && this.slaytlar.length > 1;

    this.azHareket = matchMedia('(prefers-reduced-motion: reduce)');
    if (this.azHareket.matches) this.otomatik = false;

    this.style.setProperty('--bc-hero-sure', `${this.sure}ms`);

    this.querySelector('[data-bc-onceki]')?.addEventListener('click', () => this.#git(this.#index - 1));
    this.querySelector('[data-bc-sonraki]')?.addEventListener('click', () => this.#git(this.#index + 1));
    this.cubuklar.forEach((c, i) => c.addEventListener('click', () => this.#git(i)));

    this.#klavye();
    this.#kaydirma();
    this.#duraklatmaKurallari();
    this.#gecHidrasyon();

    this.#uygula(0);
    this.#basla();
  }

  disconnectedCallback() {
    clearTimeout(this.#zamanlayici);
  }

  /* ---------- geçiş ---------- */

  #git(hedef) {
    const n = this.slaytlar.length;
    const i = ((hedef % n) + n) % n;
    // Kullanıcı `load` olayından önce tıklamış olabilir → hedefi hemen doldur
    this.#hidrat(this.slaytlar[i]);
    this.#uygula(i);
    this.#basla();
  }

  #uygula(i) {
    this.#index = i;

    // Bir sonraki slaydı şimdiden hazırla ki çapraz solmada boş kutu olmasın.
    // ⚠️ İLK ÇİZİMDE ATLANIR: orada yapılırsa 2. slayt daha sayfa yüklenirken
    // inmeye başlıyor ve ilk slaytla (LCP) bant genişliği için yarışıyor —
    // düzeltmeye çalıştığımız sorunun ta kendisi. İlk çizimden sonrasını
    // `#gecHidrasyon` zaten `load` olayının ardından hallediyor.
    if (this.#ilkCizimBitti) {
      this.#hidrat(this.slaytlar[(i + 1) % this.slaytlar.length]);
    }
    this.#ilkCizimBitti = true;

    this.slaytlar.forEach((s, k) => {
      const aktif = k === i;
      s.classList.toggle('is-active', aktif);
      s.setAttribute('aria-hidden', aktif ? 'false' : 'true');
      // Pasif slayttaki bağlantı klavyeyle yakalanmasın
      s.querySelectorAll('a, button').forEach((el) => {
        if (aktif) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });

    this.cubuklar.forEach((c, k) => {
      c.classList.toggle('is-active', k === i);
      c.setAttribute('aria-current', k === i ? 'true' : 'false');
      const dolgu = c.querySelector('[data-bc-dolgu]');
      if (!dolgu) return;
      // CSS animasyonunu yeniden başlat (reflow şart)
      dolgu.style.animation = 'none';
      void dolgu.offsetWidth;
      dolgu.style.animation = '';
    });
  }

  /* ---------- görsel hidrasyonu ---------- */

  /**
   * ⚠️ NEDEN VAR: Slaytlar `position:absolute; inset:0` ile üst üste duruyor,
   * yani tarayıcıya göre HEPSİ ekranın içinde → `loading="lazy"` hiçbir şeyi
   * ertelemiyor ve üç slayt da ilk boyamada tam boyutta iniyordu (ölçüm
   * 2026-08-15: 3 × ~900 KB ≈ 2.7 MB, ilk slayt diğer ikisiyle bant genişliği
   * için yarıştığı için geç geliyordu).
   * Çözüm: ilk slayt dışındakilerin URL'leri Liquid'de `data-bc-*` içinde
   * bekliyor; burada gerçek attribute'a çevriliyor.
   *
   * ⚠️ SIRA ÖNEMLİ: önce `<source>`, sonra `<img>`. Tarayıcı `<picture>`
   * seçimini ancak img'in srcset/src'si değişince yeniden çalıştırıyor —
   * img önce doldurulursa source'lar hesaba katılmadan mobil aday seçilir.
   * querySelectorAll belge sırası döndürdüğü için sıra kendiliğinden doğru.
   */
  #hidrat(slayt) {
    if (!slayt || slayt.dataset.bcHidre === '1') return;
    slayt.dataset.bcHidre = '1';

    slayt.querySelectorAll('[data-bc-srcset], [data-bc-src]').forEach((el) => {
      if (el.dataset.bcSrcset) {
        el.setAttribute('srcset', el.dataset.bcSrcset);
        delete el.dataset.bcSrcset;
      }
      if (el.dataset.bcSrc) {
        el.setAttribute('src', el.dataset.bcSrc);
        delete el.dataset.bcSrc;
      }
    });
  }

  /** Sayfa yükü bitene kadar bekle — ilk slayt (LCP) bant genişliğini paylaşmasın. */
  #gecHidrasyon() {
    const hepsi = () => this.slaytlar.forEach((s) => this.#hidrat(s));
    const ertele = () => setTimeout(hepsi, 250);

    if (document.readyState === 'complete') ertele();
    else addEventListener('load', ertele, { once: true });
  }

  /* ---------- otomatik geçiş ---------- */

  #basla() {
    clearTimeout(this.#zamanlayici);
    if (!this.otomatik || this.#duraklat.size > 0) return;
    this.classList.remove('is-paused');
    this.#zamanlayici = setTimeout(() => this.#git(this.#index + 1), this.sure);
  }

  #dur(sebep) {
    this.#duraklat.add(sebep);
    this.classList.add('is-paused');
    clearTimeout(this.#zamanlayici);
  }

  #devam(sebep) {
    this.#duraklat.delete(sebep);
    if (this.#duraklat.size === 0) this.#basla();
  }

  #duraklatmaKurallari() {
    this.addEventListener('pointerenter', () => this.#dur('fare'));
    this.addEventListener('pointerleave', () => this.#devam('fare'));
    this.addEventListener('focusin', () => this.#dur('odak'));
    this.addEventListener('focusout', () => this.#devam('odak'));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.#dur('sekme');
      else this.#devam('sekme');
    });
  }

  /* ---------- girdi ---------- */

  #klavye() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.#git(this.#index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.#git(this.#index + 1); }
    });
  }

  #kaydirma() {
    let x0 = null;
    let y0 = null;

    this.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return;
      x0 = e.clientX;
      y0 = e.clientY;
    }, { passive: true });

    this.addEventListener('pointerup', (e) => {
      if (x0 === null) return;
      const dx = e.clientX - x0;
      const dy = e.clientY - y0;
      x0 = null;
      // Dikey kaydırmayı çalmayalım
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        this.#git(this.#index + (dx < 0 ? 1 : -1));
      }
    }, { passive: true });
  }
}

if (!customElements.get('bc-hero-slider')) {
  customElements.define('bc-hero-slider', BcHeroSlider);
}
